/**
 * THE BUILT INDEX™ — single source of truth for the multi-assessment platform.
 *
 * Four diagnostics run in sequence:
 *   1. The Built Diagnostic™ (10 MAKECHANGE principles, 1–10 each, /100)
 *   2. DISC leadership style (forced choice)
 *   3. Fuel & Drain (love-language framing for leaders)
 *   4. LOVIN Listening Scale (5 dimensions, 1–10 each, /50)
 *
 * Scoring, tier interpretation, profiles, pricing and external URLs all live
 * here. Never hard-code a Built Index price, question or URL in a component.
 */

import { ROUTES } from "@/content/offers";

/* -------------------------------------------------------------------------- */
/* 1. THE BUILT DIAGNOSTIC™                                                    */
/* -------------------------------------------------------------------------- */

export const BUILT_PRINCIPLES = [
  {
    key: "momentum",
    letter: "M",
    name: "Momentum",
    description: "Forward motion in last 90 days",
    longDescription:
      "Are you in forward motion on purpose, or just busy? Momentum measures whether your last 90 days produced visible, intentional movement toward your vision.",
    prompt:
      "Over the last 90 days, my leadership has produced clear, intentional forward motion — not busy work, but visible progress.",
  },
  {
    key: "authority",
    letter: "A",
    name: "Authority",
    description: "Grounded identity, not borrowed title",
    longDescription:
      "Authority is identity-rooted, not title-borrowed. It does not collapse when the title is removed.",
    prompt:
      "My authority comes from who I am, not the title I hold. I do not rely on borrowed power.",
  },
  {
    key: "kingdomFocus",
    letter: "K",
    name: "Kingdom-Focus",
    description: "Bigger-table orientation",
    longDescription:
      "Kingdom-Focus means building a table bigger than the next quarter — assets, people, and structures that outlive you.",
    prompt:
      "I am building a table bigger than my quarter, my title, or my tenure — focused on legacy, not just performance.",
  },
  {
    key: "equity",
    letter: "E",
    name: "Equity",
    description: "Architectural fairness",
    longDescription:
      "Equity is not a slogan — it is architecture. The system, not the speech, decides if fairness is real.",
    prompt:
      "The systems I lead produce fair access, fair voice, and fair outcomes — not just stated values.",
  },
  {
    key: "clarity",
    letter: "C",
    name: "Clarity",
    description: "Team can repeat your vision",
    longDescription:
      "If your team cannot repeat your vision back to you in their own words, you do not have clarity — you have noise.",
    prompt:
      "Any member of my team can repeat my vision and the next move in their own words without prompting.",
  },
  {
    key: "healing",
    letter: "H",
    name: "Healing",
    description: "Addressed leakage points",
    longDescription:
      "Healing is not avoidance. It is addressing the leakage points — the unhealed places that quietly cost you people, money, and trust.",
    prompt:
      "I have identified and addressed the personal leakage points that used to leak poison into my leadership.",
  },
  {
    key: "action",
    letter: "A",
    name: "Action",
    description: "Closing the knowing-doing gap",
    longDescription:
      "Knowing is not enough. Action closes the gap between what you know and what you do — consistently.",
    prompt:
      "I close the gap between what I know I should do and what I actually do — without dragging or delaying.",
  },
  {
    key: "navigation",
    letter: "N",
    name: "Navigation",
    description: "Active steering, refusing drift",
    longDescription:
      "Drift is not neutral — it is the default. Navigation is active steering: constant correction, constant clarity.",
    prompt:
      "I actively steer my organization and my life — I do not let drift, mood, or momentum make my decisions.",
  },
  {
    key: "growth",
    letter: "G",
    name: "Growth",
    description: "Designed development, never accidental",
    longDescription:
      "Growth that lasts is designed, not accidental. You can name the system that develops you and the people you lead.",
    prompt:
      "My growth and my team's growth are by design — there is a system, not a hope, producing it.",
  },
  {
    key: "empowerment",
    letter: "E",
    name: "Empowerment",
    description: "People built in last 12 months",
    longDescription:
      "Built people build built people. Empowerment is measured in named individuals you have moved from broken to built in the last 12 months.",
    prompt:
      "In the last 12 months, I can name specific people I moved from broken or stuck to built and producing.",
  },
] as const;

export type BuiltKey = (typeof BUILT_PRINCIPLES)[number]["key"];

export const BUILT_INTERPRETATION = [
  { min: 80, max: 100, tier: "Built", label: "Built leader operating at executive capacity." },
  { min: 60, max: 79, tier: "Building", label: "Building leader — gaps are workable." },
  { min: 40, max: 59, tier: "Mixed", label: "Mixed — significant principles need attention." },
  { min: 0, max: 39, tier: "Tax", label: "System is paying a high tax. Intervention needed." },
] as const;

/* -------------------------------------------------------------------------- */
/* 2. DISC                                                                     */
/* -------------------------------------------------------------------------- */

export const DISC_QUESTIONS = [
  {
    id: "d1",
    prompt: "In a high-pressure decision, I am most likely to:",
    options: {
      D: "Take charge and decide quickly",
      I: "Rally people and inspire action",
      S: "Stabilize the team and listen first",
      C: "Analyze the data before moving",
    },
  },
  {
    id: "d2",
    prompt: "My natural communication style is:",
    options: {
      D: "Direct and to the point",
      I: "Expressive and enthusiastic",
      S: "Patient and warm",
      C: "Precise and detailed",
    },
  },
  {
    id: "d3",
    prompt: "When leading a team, I prioritize:",
    options: {
      D: "Results and winning",
      I: "Influence and energy",
      S: "Harmony and loyalty",
      C: "Standards and accuracy",
    },
  },
  {
    id: "d4",
    prompt: "Conflict makes me want to:",
    options: {
      D: "Confront it head-on",
      I: "Talk it out and reconnect",
      S: "Smooth it over and protect peace",
      C: "Step back and assess facts",
    },
  },
  {
    id: "d5",
    prompt: "My biggest leadership strength is:",
    options: {
      D: "Decisive action",
      I: "Inspiring vision",
      S: "Steady consistency",
      C: "Quality control",
    },
  },
  {
    id: "d6",
    prompt: "Under stress, I tend to:",
    options: {
      D: "Push harder, demand more",
      I: "Talk more, sell harder",
      S: "Withdraw and stabilize",
      C: "Over-analyze and delay",
    },
  },
  {
    id: "d7",
    prompt: "I am most energized by:",
    options: {
      D: "Challenges to overcome",
      I: "People and connection",
      S: "Stable, supportive environments",
      C: "Mastery and competence",
    },
  },
  {
    id: "d8",
    prompt: "My pace of work is best described as:",
    options: {
      D: "Fast and driving",
      I: "Fast and social",
      S: "Steady and measured",
      C: "Methodical and careful",
    },
  },
] as const;

export const DISC_PROFILES = {
  D: {
    name: "Dominance",
    tagline: "The Driver",
    summary:
      "Direct, decisive, and results-focused. You lead with conviction and move fast. Your edge is speed; your tax is people who feel run over when you forget to slow down.",
    builtMove:
      "Pair your decisiveness with intentional pauses. Decisive leaders who listen build empires; decisive leaders who don't burn them.",
  },
  I: {
    name: "Influence",
    tagline: "The Catalyst",
    summary:
      "Persuasive, optimistic, and people-energizing. You light up rooms and move teams through belief. Your edge is influence; your tax is follow-through when the energy fades.",
    builtMove:
      "Build systems behind your charisma. Energy without execution is performance. Energy with execution is leadership.",
  },
  S: {
    name: "Steadiness",
    tagline: "The Anchor",
    summary:
      "Patient, loyal, and consistent. You stabilize teams and hold the line under pressure. Your edge is reliability; your tax is conflict avoidance and slow change.",
    builtMove:
      "Practice naming the hard truth quickly. Loyalty without honesty becomes complicity.",
  },
  C: {
    name: "Conscientiousness",
    tagline: "The Architect",
    summary:
      "Analytical, precise, and quality-driven. You build with rigor and refuse sloppy outcomes. Your edge is excellence; your tax is paralysis and over-correction.",
    builtMove:
      "Set a 'good enough to ship' bar. Perfection in private is invisible; shipped excellence multiplies.",
  },
} as const;

export type DiscType = keyof typeof DISC_PROFILES;

/* -------------------------------------------------------------------------- */
/* 3. FUEL & DRAIN                                                             */
/* -------------------------------------------------------------------------- */

export const LOVE_LANGUAGES = [
  { key: "words", name: "Words of Affirmation" },
  { key: "acts", name: "Acts of Service" },
  { key: "gifts", name: "Receiving Gifts" },
  { key: "time", name: "Quality Time" },
  { key: "touch", name: "Physical Presence" },
] as const;

export type LoveLanguageKey = (typeof LOVE_LANGUAGES)[number]["key"];

export const LOVE_QUESTIONS = [
  {
    id: "l1",
    prompt: "I feel most fueled when someone:",
    options: {
      words: "Tells me specifically what they appreciate about me",
      acts: "Does something practical to lighten my load",
      gifts: "Surprises me with a thoughtful gesture or gift",
      time: "Gives me their undivided, unhurried attention",
      touch: "Is physically present beside me in the work",
    },
  },
  {
    id: "l2",
    prompt: "I feel most drained when someone:",
    options: {
      words: "Stays silent or critical instead of affirming",
      acts: "Talks about helping but never follows through",
      gifts: "Forgets meaningful occasions or thoughtful gestures",
      time: "Is physically present but mentally elsewhere",
      touch: "Keeps emotional and physical distance",
    },
  },
  {
    id: "l3",
    prompt: "Looking back, the moments I felt most valued were when someone:",
    options: {
      words: "Spoke words I still remember years later",
      acts: "Showed up and did the work without being asked",
      gifts: "Gave me something that proved they were paying attention",
      time: "Cleared their schedule to be fully with me",
      touch: "Stood close or shared space without words",
    },
  },
  {
    id: "l4",
    prompt: "When I want to show someone I care, I usually:",
    options: {
      words: "Tell them, write them, encourage them out loud",
      acts: "Do things for them — fix, build, handle, deliver",
      gifts: "Find the right thing and give it",
      time: "Make uninterrupted time for them",
      touch: "Show up in person, share space, be near",
    },
  },
  {
    id: "l5",
    prompt: "The fastest way to drain my battery as a leader is:",
    options: {
      words: "Constant criticism or silence on what's working",
      acts: "Promises made and never kept",
      gifts: "Feeling overlooked or unappreciated in tangible ways",
      time: "Being interrupted constantly or never getting deep work",
      touch: "Isolation — leading alone, no team in the trenches with me",
    },
  },
] as const;

export const LOVE_PROFILES: Record<
  LoveLanguageKey,
  { fuel: string; drain: string; booklet: string }
> = {
  words: {
    fuel: "Specific, spoken affirmation",
    drain: "Silence or criticism in place of acknowledgment",
    booklet:
      "Words are not flattery to you — they are fuel. Spoken affirmation lands as proof you are seen. Build a leadership rhythm where you speak appreciation OUT LOUD to your team, and tell your inner circle exactly what you need to hear. Drain warning: silence and unspoken criticism will burn you out faster than overwork.",
  },
  acts: {
    fuel: "Practical service and follow-through",
    drain: "Empty promises and talk without action",
    booklet:
      "Action is your language. You feel valued when people DO — fix it, handle it, ship it, show up. Talk without follow-through reads as dismissal. Surround yourself with execution-grade people and protect yourself from chronic over-promisers. Drain warning: stop confusing eloquence with effort.",
  },
  gifts: {
    fuel: "Thoughtful tokens that prove attention",
    drain: "Being overlooked or unrecognized",
    booklet:
      "Gifts to you are not materialism — they are memory. A thoughtful gift says: I was thinking about you when you weren't in the room. Build rituals of recognition into your leadership. Drain warning: protect yourself from rooms that consistently overlook contribution.",
  },
  time: {
    fuel: "Undivided, unhurried presence",
    drain: "Being interrupted or half-attended-to",
    booklet:
      "Time is your fuel — but only protected, fully-present time. Distracted time costs you more than no time at all. Build deep-work blocks, undistracted family blocks, and undistracted mentorship blocks into your week. Drain warning: shallow, interrupted days will hollow you out even if the calendar looks full.",
  },
  touch: {
    fuel: "Physical presence and proximity",
    drain: "Isolation and distance",
    booklet:
      "Presence is your fuel. You lead better in-person, build better in-person, and recover better in-person. Remote-only leadership is a slow tax on you. Build in-person rhythms — retreats, side-by-side work sessions. Drain warning: prolonged isolation is not just preference, it is wiring.",
  },
};

/* -------------------------------------------------------------------------- */
/* 4. LOVIN LISTENING SCALE                                                    */
/* -------------------------------------------------------------------------- */

export const LOVIN_DIMENSIONS = [
  { key: "loving", letter: "L", name: "Loving", prompt: "I listen with genuine care for the person, not just the message." },
  { key: "open", letter: "O", name: "Open", prompt: "I listen without assuming I already know what they will say." },
  { key: "validating", letter: "V", name: "Validating", prompt: "I make people feel heard before I respond, even when I disagree." },
  { key: "intentional", letter: "I", name: "Intentional", prompt: "I listen to understand and serve, not to win or fix." },
  { key: "nonreactive", letter: "N", name: "Non-reactive", prompt: "I stay calm and present when what I hear triggers me." },
] as const;

export type LovinKey = (typeof LOVIN_DIMENSIONS)[number]["key"];

export const LOVIN_TIERS = [
  { min: 45, max: 50, tier: "Master Listener", description: "You hear what others miss. People feel safe with you." },
  { min: 35, max: 44, tier: "Strong Listener", description: "Solid foundation. Sharpen the edges — especially under pressure." },
  { min: 25, max: 34, tier: "Developing Listener", description: "You hear words but miss meaning. Focus on validation before response." },
  { min: 0, max: 24, tier: "Reactive Listener", description: "You are responding, not receiving. This is a leadership liability." },
] as const;

/* -------------------------------------------------------------------------- */
/* Scoring                                                                     */
/* -------------------------------------------------------------------------- */

export function calculateBuiltTotal(scores: Partial<Record<BuiltKey, number>>): number {
  return BUILT_PRINCIPLES.reduce((sum, p) => sum + (scores[p.key] || 0), 0);
}

export function getBuiltTier(total: number): (typeof BUILT_INTERPRETATION)[number] {
  return (
    BUILT_INTERPRETATION.find((t) => total >= t.min && total <= t.max) ||
    BUILT_INTERPRETATION[BUILT_INTERPRETATION.length - 1]
  );
}

export function calculateDiscType(answers: Record<string, string>): {
  type: DiscType;
  scores: Record<DiscType, number>;
} {
  const scores: Record<DiscType, number> = { D: 0, I: 0, S: 0, C: 0 };
  Object.values(answers).forEach((v) => {
    if (v === "D" || v === "I" || v === "S" || v === "C") scores[v] += 1;
  });
  const type = (Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0]) as DiscType;
  return { type, scores };
}

const DRAIN_QUESTION_IDS = new Set(["l2", "l5"]);

export function calculateLoveProfile(answers: Record<string, string>): {
  primaryFuel: LoveLanguageKey;
  primaryDrain: LoveLanguageKey;
  fuelScores: Record<string, number>;
  drainScores: Record<string, number>;
} {
  const fuelScores: Record<string, number> = { words: 0, acts: 0, gifts: 0, time: 0, touch: 0 };
  const drainScores: Record<string, number> = { words: 0, acts: 0, gifts: 0, time: 0, touch: 0 };
  Object.entries(answers).forEach(([qid, lang]) => {
    const bucket = DRAIN_QUESTION_IDS.has(qid) ? drainScores : fuelScores;
    if (lang in bucket) bucket[lang] += 1;
  });
  const primaryFuel = Object.entries(fuelScores).sort((a, b) => b[1] - a[1])[0][0] as LoveLanguageKey;
  const primaryDrain = Object.entries(drainScores).sort((a, b) => b[1] - a[1])[0][0] as LoveLanguageKey;
  return { primaryFuel, primaryDrain, fuelScores, drainScores };
}

export function calculateLovinScore(answers: Partial<Record<LovinKey, number>>): {
  score: number;
  tier: string;
  description: string;
} {
  const score = LOVIN_DIMENSIONS.reduce((s, d) => s + (answers[d.key] || 0), 0);
  const match =
    LOVIN_TIERS.find((t) => score >= t.min && score <= t.max) ||
    LOVIN_TIERS[LOVIN_TIERS.length - 1];
  return { score, tier: match.tier, description: match.description };
}

export function getDominantPrinciple(scores: Partial<Record<BuiltKey, number>>): string {
  const sorted = [...BUILT_PRINCIPLES].sort(
    (a, b) => (scores[b.key] || 0) - (scores[a.key] || 0),
  );
  return sorted[0]?.name ?? "Momentum";
}

export function getWeakestPrinciple(scores: Partial<Record<BuiltKey, number>>): string {
  const sorted = [...BUILT_PRINCIPLES].sort(
    (a, b) => (scores[a.key] || 0) - (scores[b.key] || 0),
  );
  return sorted[0]?.name ?? "Healing";
}

/* -------------------------------------------------------------------------- */
/* Pricing, routes and external URLs                                           */
/* -------------------------------------------------------------------------- */

/** Stripe lookup key for the $27 full-report unlock. */
export const BUILT_INDEX_PRICE_ID = "built_index_full_report_onetime";
export const FULL_REPORT_PRICE_USD = 27;
export const ORIGINAL_REPORT_PRICE_USD = 47;
export const FOUNDERS_LAUNCH_LIMIT = 100;

/** Free digital copy of Built On Purpose, unlocked with the paid report. */
export const BUILT_BOOK_URL =
  "https://designrr.page/?id=503536&token=2954250591&type=FP&h=3484";

export const BUILT_INDEX_ROUTES = {
  landing: "/built-index",
  start: "/built-index/start",
  built: "/built-index/diagnostic",
  disc: "/built-index/disc",
  love: "/built-index/fuel-drain",
  lovin: "/built-index/listening",
  results: "/built-index/results",
  admin: "/admin/built-index",
} as const;

/** Ordered assessment steps used by progress indicators and routing. */
export const BUILT_INDEX_STEPS = [
  { path: BUILT_INDEX_ROUTES.built, label: "The Built Diagnostic" },
  { path: BUILT_INDEX_ROUTES.disc, label: "Leadership Style" },
  { path: BUILT_INDEX_ROUTES.love, label: "Fuel & Drain" },
  { path: BUILT_INDEX_ROUTES.lovin, label: "Listening Scale" },
] as const;

/** 1:1 booking destination for the coaching upsell. */
export const BUILT_INDEX_BOOKING_HREF = ROUTES.strategySession;
